import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import MobileBottomBar from '../../components/feature/MobileBottomBar';
import { useEffect } from 'react';
import { useSEO } from '../../utils/seo';

const SingleItemPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://danvanremovals.co.uk';
  
  useSEO({
    title: 'Single Item Moves Lancashire | Furniture & Appliance Transport | Dan & Van',
    description: 'Need one item moved? Sofas, wardrobes, washing machines, pianos. Same-day service available. Fully insured furniture transport — 07530 166035',
    keywords: 'single item moves, furniture transport, appliance delivery, sofa moving, piano moving, same day delivery Lancashire',
    canonical: '/single-item-moves',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Single Item & Appliance Moves',
      provider: {
        '@type': 'MovingCompany',
        name: 'Dan & Van Removals',
        telephone: '07530166035',
        areaServed: 'Lancashire, UK',
      },
      description: 'Specialist single item moving service across Lancashire. From sofas to washing machines, pianos to wardrobes. Same-day service often available.',
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        priceRange: '££',
      },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const items = [
    'Sofas and armchairs',
    'Wardrobes and cabinets',
    'Beds and mattresses',
    'Dining tables and chairs',
    'Washing machines and dryers',
    'Fridges and freezers',
    'Pianos and organs',
    'Gym equipment',
    'Garden furniture',
    'Antiques and valuables',
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <MobileBottomBar />

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden mt-20">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/8f69829654c77fd4eff7cce7dbf036df.jpg')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-gold text-xs uppercase tracking-wider mb-4"
          >
            <Link to="/" className="hover:underline cursor-pointer">Home</Link> / Services / Single Item Moves
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="font-sans text-6xl font-bold mb-6"
          >
            Single Item & Appliance Moves
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-xl text-white/90 mb-8"
          >
            Specialist furniture and appliance transportation
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <Link
              to="/contact"
              className="inline-block px-10 py-4 bg-gold text-charcoal font-semibold text-lg rounded hover:shadow-luxury-hover transition-all whitespace-nowrap cursor-pointer"
            >
              Get a Quote
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <h2 className="font-sans text-4xl font-bold text-charcoal mb-6">
                Need just one item moved? We've got you covered
              </h2>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Not every move requires a full removal service. Sometimes you just need a single piece of furniture or an appliance transported safely from A to B. That's where our single item moving service comes in.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Whether you've bought a new sofa, need to move a washing machine, or want to transport an antique piano, we have the expertise and equipment to handle it with care. No job is too small for Dan & Van Removals.
              </p>

              <h3 className="font-sans text-3xl font-bold text-charcoal mb-6 mt-12">
                Items we regularly move
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
                {items.map((item, index) => (
                  <div key={index} className="flex items-center">
                    <i className="ri-checkbox-circle-fill text-gold text-xl mr-3"></i>
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>

              <h3 className="font-sans text-3xl font-bold text-charcoal mb-6">
                Why choose us for single item moves?
              </h3>

              <ul className="space-y-4 mb-12">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-gold text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">
                    <strong className="text-charcoal">Specialist equipment:</strong> Trolleys, straps, and protective blankets for safe handling
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-gold text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">
                    <strong className="text-charcoal">Experienced team:</strong> Trained in proper lifting and manoeuvring techniques
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-gold text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">
                    <strong className="text-charcoal">Fully insured:</strong> Your item is covered throughout the journey
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-gold text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">
                    <strong className="text-charcoal">Flexible scheduling:</strong> Same-day and next-day service often available
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-gold text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">
                    <strong className="text-charcoal">Competitive pricing:</strong> Fair rates for single item transportation
                  </span>
                </li>
              </ul>

              <h3 className="font-sans text-3xl font-bold text-charcoal mb-6">
                How it works
              </h3>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Simply tell us what you need moved, where it's going from and to, and when you need it done. We'll give you an instant quote with no hidden fees. If you're happy to proceed, we'll arrange a convenient time and handle everything from there.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                We'll arrive with the right equipment, carefully load your item, transport it safely, and place it exactly where you want it in your new location. It's that simple.
              </p>

              <div className="bg-gray-50 border-l-4 border-gold p-6 rounded">
                <p className="text-lg text-charcoal font-medium mb-2">
                  Need a single item moved today?
                </p>
                <p className="text-gray-700 mb-4">
                  Call us now for same-day or next-day service. We're often able to accommodate urgent requests.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="tel:07530166035"
                    className="px-6 py-3 border-2 border-charcoal text-charcoal font-semibold rounded hover:border-gold hover:text-gold transition-all text-center whitespace-nowrap cursor-pointer"
                  >
                    Call 07530 166035
                  </a>
                  <Link
                    to="/contact"
                    className="px-6 py-3 bg-gold text-charcoal font-semibold rounded hover:shadow-luxury-hover transition-all text-center whitespace-nowrap cursor-pointer"
                  >
                    Get a Quote
                  </Link>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="bg-white border border-gold/20 rounded-lg p-8 shadow-luxury mb-8">
                  <h3 className="font-sans text-2xl font-bold text-charcoal mb-6">
                    Get Your Free Quote
                  </h3>
                  <p className="text-gray-700 mb-6">
                    Contact us directly for a fast, free quote on your single item move.
                  </p>
                  <div className="space-y-3">
                    <a
                      href="https://wa.me/447530166035"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-full py-3 bg-green-600 text-white font-semibold rounded hover:bg-green-700 transition-all whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-whatsapp-line mr-2 text-xl"></i>
                      WhatsApp Us
                    </a>
                    <a
                      href="tel:07530166035"
                      className="flex items-center justify-center w-full py-3 border-2 border-charcoal text-charcoal font-semibold rounded hover:border-gold hover:text-gold transition-all whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-phone-line mr-2 text-lg"></i>
                      Call Now
                    </a>
                  </div>
                </div>

                <div className="bg-gold/10 border border-gold/30 rounded-lg p-6">
                  <h4 className="font-sans font-bold text-charcoal mb-3">
                    Same-day service available
                  </h4>
                  <p className="text-sm text-gray-700 mb-4">
                    Need something moved urgently? We often have availability for same-day and next-day moves. Call us to check.
                  </p>
                  <a
                    href="tel:07530166035"
                    className="block w-full py-3 text-center bg-gold text-charcoal font-semibold rounded hover:shadow-luxury-hover transition-all whitespace-nowrap cursor-pointer"
                  >
                    Call Now
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SingleItemPage;
